import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeContextValue {
  mode: ThemeMode;
  isDark: boolean;
  setMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function getSystemPrefersDark(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function applyClassToDocument(isDark: boolean) {
  const root = document.documentElement;
  if (isDark) {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    const stored = localStorage.getItem('themeMode') as ThemeMode | null;
    return stored ?? 'system';
  });

  // Tracked as state, not derived once: the system preference can change
  // while the app is open. Previously the media listener only rewrote the
  // document class, so Tailwind flipped to dark while MUI's palette stayed
  // light - the two disagreed until a full reload.
  const [systemPrefersDark, setSystemPrefersDark] = useState(getSystemPrefersDark);

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (event: MediaQueryListEvent) => setSystemPrefersDark(event.matches);
    media.addEventListener?.('change', handler);
    // Re-read on mount in case it changed before the listener attached.
    setSystemPrefersDark(media.matches);
    return () => media.removeEventListener?.('change', handler);
  }, []);

  const isDark = useMemo(
    () => mode === 'dark' || (mode === 'system' && systemPrefersDark),
    [mode, systemPrefersDark],
  );

  useEffect(() => {
    localStorage.setItem('themeMode', mode);
    applyClassToDocument(isDark);
  }, [mode, isDark]);

  const value = useMemo(() => ({ mode, isDark, setMode }), [mode, isDark]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export function useThemeMode(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useThemeMode must be used within ThemeProvider');
  return ctx;
}

